import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SubscriptionTier } from '../../types/subscriptionTier';
import { getTiers } from '../../services/SubscriptionTierService';

interface PackageFeature {
  name: string;
  values: Record<string, string | number>; // Dynamic values keyed by tier name
}

export const PackagesPage: React.FC = () => {
  const [isAnnual, setIsAnnual] = useState(false);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTiers = async () => {
      try {
        setLoading(true);
        const fetchedTiers = await getTiers();
        setTiers(fetchedTiers);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch tiers:', err);
        setError('Failed to load subscription packages. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTiers();
  }, []);

  // Rebuild feature matrix when isAnnual changes
  useEffect(() => {
    // This will trigger a re-render with updated prices
  }, [isAnnual, tiers]);

  // Calculate annual prices (16% discount)
  const getAnnualPrice = (monthlyPrice: string) => {
    const price = parseFloat(monthlyPrice.replace('$', '').replace(',', ''));
    if (isNaN(price)) return monthlyPrice;
    const annualPrice = price * 12 * 0.84; // 16% discount
    return `$${Math.round(annualPrice)}`;
  };

  // Build feature matrix from tiers
  const buildFeatureMatrix = (): PackageFeature[] => {
    if (!tiers.length) return [];

    const tierMap: Record<string, SubscriptionTier> = {};
    tiers.forEach(tier => {
      tierMap[tier.name.toLowerCase()] = tier;
    });

    const features: PackageFeature[] = [
      {
        name: 'Subscription Price',
        values: {}
      },
      {
        name: 'SMS Credits (Annual)',
        values: {}
      },
      {
        name: 'Single SMS messages',
        values: {}
      },
      {
        name: 'Bulk Campaigns',
        values: {}
      },
      {
        name: 'Additional SMS credits - annual plan',
        values: {}
      },
      {
        name: 'Additional SMS credits - monthly plan',
        values: {}
      },
      {
        name: 'One time setup fee',
        values: {}
      },
      {
        name: 'Custom Sender ID',
        values: {}
      },
      {
        name: 'Message Templates',
        values: {}
      },
      {
        name: 'Users',
        values: {}
      },
      {
        name: 'Shared templates',
        values: {}
      },
      {
        name: 'Shared contacts',
        values: {}
      },
      {
        name: 'Scheduled Messages',
        values: {}
      },
      {
        name: 'Recurring Messages',
        values: {}
      },
      {
        name: 'Usage reporting',
        values: {}
      },
      {
        name: 'Scheduled reports',
        values: {}
      },
      {
        name: 'API Access',
        values: {}
      },
      {
        name: 'Support',
        values: {}
      }
    ];

    tiers.forEach(tier => {
      const tierKey = tier.name.toLowerCase();
      features[0].values[tierKey] = isAnnual ? getAnnualPrice(tier.priceMonthly) : tier.priceMonthly;
      features[1].values[tierKey] = tier.smsCreditsAnnual;
      features[2].values[tierKey] = tier.singleSmsMessages ? '✓' : '-';
      features[3].values[tierKey] = tier.bulkCampaigns ? '✓' : '-';
      features[4].values[tierKey] = tier.additionalSmsCreditPriceAnnual;
      features[5].values[tierKey] = tier.additionalSmsCreditPriceMonthly;
      features[6].values[tierKey] = tier.oneTimeSetupFee;
      features[7].values[tierKey] = tier.customSenderId ? '✓' : '-';
      features[8].values[tierKey] = tier.messageTemplates;
      features[9].values[tierKey] = tier.users;
      features[10].values[tierKey] = tier.sharedTemplates ? '✓' : '-';
      features[11].values[tierKey] = tier.sharedContacts ? '✓' : '-';
      features[12].values[tierKey] = tier.scheduledMessages ? '✓' : '-';
      features[13].values[tierKey] = tier.recurringMessages ? '✓' : '-';
      features[14].values[tierKey] = tier.usageReporting ? '✓' : '-';
      features[15].values[tierKey] = tier.scheduledReports ? '✓' : '-';
      features[16].values[tierKey] = tier.apiAccess ? '✓' : '-';
      features[17].values[tierKey] = tier.support;
    });

    return features;
  };

  const features = buildFeatureMatrix();
  const sortedTiers = [...tiers].sort((a, b) => a.sortIndex - b.sortIndex);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  // Get value for a tier in a feature row
  const getTierValue = (feature: PackageFeature, tierName: string): string | number => {
    const tierKey = tierName.toLowerCase();
    return feature.values[tierKey] ?? '-';
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="block hover:opacity-90 transition-opacity">
            <img 
              src="/images/logo.jpg"
              alt="Rukuse Technology"
              className="h-14 w-auto"
            />
          </Link>
          <div className="flex items-center space-x-2 text-sm">
            <span className="text-gray-600">Already have an account?</span>
            <Link to="/" className="text-[#FF5722] hover:underline">Log in</Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          {/* Banner Image Section */}
          <div className="mb-12">
            <img 
              src={process.env.PUBLIC_URL + '/images/packagesbannerone.jpeg'}
              alt="SMS Packages"
              className="w-3/4 h-auto mx-auto rounded-lg shadow-md"
            />
          </div>

          {/* Text Content Section */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Choose the Perfect SMS Package for Your Business
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Select from our range of SMS packages designed to meet your business communication needs. Whether you're a small business or a large enterprise, we have the right package for you.
            </p>
            
            {/* Call to Action Buttons */}
            <div className="flex items-center justify-center gap-4 mt-6">
              <button className="bg-[#FF5722] text-white px-6 py-2.5 rounded-md hover:bg-[#F4511E] transition-colors duration-200">
                Get Started
              </button>
              <button className="border-2 border-[#FF5722] text-[#FF5722] px-6 py-2.5 rounded-md hover:bg-[#FF5722] hover:text-white transition-colors duration-200">
                Start Demo
              </button>
            </div>
          </div>

          {/* Billing Toggle */}
          <div className="flex justify-center items-center mb-8">
            <div className="bg-gray-100 p-1 rounded-lg inline-flex items-center">
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                  !isAnnual 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                onClick={() => setIsAnnual(false)}
              >
                Monthly
              </button>
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isAnnual 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                onClick={() => setIsAnnual(true)}
              >
                Annually
              </button>
            </div>
            {isAnnual && (
              <span className="ml-3 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                Save 16%
              </span>
            )}
          </div>

          {/* Packages Table */}
          {loading ? (
            <div className="bg-white rounded-lg shadow-lg p-12 text-center">
              <p className="text-gray-600">Loading subscription packages...</p>
            </div>
          ) : error ? (
            <div className="bg-white rounded-lg shadow-lg p-12 text-center">
              <p className="text-red-600">{error}</p>
            </div>
          ) : sortedTiers.length === 0 ? (
            <div className="bg-white rounded-lg shadow-lg p-12 text-center">
              <p className="text-gray-600">No subscription packages available at this time.</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="py-4 px-6 text-left text-sm font-medium text-gray-700 border-r-2 border-[#FF5722]">
                        DEFINITION
                      </th>
                      {sortedTiers.map((tier, index) => (
                        <th
                          key={tier.id}
                          className={`py-4 px-6 text-center text-sm font-medium text-gray-700 ${
                            index < sortedTiers.length - 1 ? 'border-r-2 border-[#FF5722]' : ''
                          }`}
                        >
                          {tier.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {features.map((feature, index) => {
                      if (feature.name === 'Subscription Price') {
                        return (
                          <React.Fragment key={feature.name}>
                            {/* Subscription Price Row */}
                            <tr className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="py-4 px-6 text-sm font-medium text-gray-900 border-r-2 border-[#FF5722]">
                                {feature.name}
                              </td>
                              {sortedTiers.map((tier, tierIndex) => (
                                <td
                                  key={tier.id}
                                  className={`py-4 px-6 text-center ${
                                    tierIndex < sortedTiers.length - 1 ? 'border-r-2 border-[#FF5722]' : ''
                                  }`}
                                >
                                  <span className="text-xl font-bold text-gray-900">
                                    {getTierValue(feature, tier.name)}
                                  </span>
                                </td>
                              ))}
                            </tr>

                            {/* CTA Buttons Row */}
                            <tr className="bg-white">
                              <td className="py-3 px-6 border-r-2 border-[#FF5722]"></td>
                              {sortedTiers.map((tier, tierIndex) => (
                                <td
                                  key={tier.id}
                                  className={`py-3 px-6 text-center ${
                                    tierIndex < sortedTiers.length - 1 ? 'border-r-2 border-[#FF5722]' : ''
                                  }`}
                                >
                                  <button className="border border-[#FF5722] text-[#FF5722] px-3 py-1.5 text-xs rounded-md hover:bg-[#FF5722] hover:text-white transition-colors duration-200">
                                    Get Started
                                  </button>
                                </td>
                              ))}
                            </tr>
                          </React.Fragment>
                        );
                      }

                      // Regular feature rows
                      return (
                        <tr key={feature.name} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="py-4 px-6 text-sm font-medium text-gray-900 border-r-2 border-[#FF5722]">
                            {feature.name}
                          </td>
                          {sortedTiers.map((tier, tierIndex) => (
                            <td
                              key={tier.id}
                              className={`py-4 px-6 text-sm text-gray-600 text-center ${
                                tierIndex < sortedTiers.length - 1 ? 'border-r-2 border-[#FF5722]' : ''
                              }`}
                            >
                              {getTierValue(feature, tier.name)}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Footnotes */}
          <div className="max-w-7xl mx-auto px-6 mt-8 space-y-2 text-sm text-gray-600 italic">
            <p>
              Phone and email support is available on support plans higher than standard,{' '}
              <a href="/support-plans" className="text-[#FF5722] hover:underline">review support plan options here</a>.
            </p>
            <p>
              Additional carrier fees may apply —{' '}
              <a href="/carrier-fees" className="text-[#FF5722] hover:underline">please click here to learn more</a>
            </p>
            <div className="mt-4 space-y-1">
              <p>* Dedicated Shortcodes are also available on request.</p>
              <p>** Additional charges apply.</p>
              <p>
                * Prices may change without prior notice.{' '}
                <a href="/sales-tax-faq" className="text-[#FF5722] hover:underline">(sales tax FAQ)</a>.
              </p>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="max-w-7xl mx-auto px-6 mt-16 mb-12">
            <h2 className="text-3xl font-bold text-center mb-8">
              Frequently asked questions.
            </h2>

            <div className="space-y-4">
              {/* FAQ Item 1 */}
              <div className="border-b border-gray-200">
                <button 
                  className="w-full flex items-center justify-between py-4 text-left"
                  onClick={() => toggleFAQ(0)}
                >
                  <div className="flex items-center">
                    <div className="w-6 h-6 flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span className="text-lg font-medium">How do I get started?</span>
                  </div>
                  <svg 
                    className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${openFAQ === 0 ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {/* Answer content */}
                {openFAQ === 0 && (
                  <div className="pb-4 px-10 text-gray-600 animate-fadeIn">
                    Select the option above that suits your business needs, and one of our experts will get you off to a great start. Or you can start with a free 7 day demo to explore our platform and its features.
                  </div>
                )}
              </div>

              {/* FAQ Item 2 */}
              <div className="border-b border-gray-200">
                <button 
                  className="w-full flex items-center justify-between py-4 text-left"
                  onClick={() => toggleFAQ(1)}
                >
                  <div className="flex items-center">
                    <div className="w-6 h-6 flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span className="text-lg font-medium">What happens at the end of my free demo?</span>
                  </div>
                  <svg 
                    className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${openFAQ === 1 ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {/* Answer content */}
                {openFAQ === 1 && (
                  <div className="pb-4 px-10 text-gray-600 animate-fadeIn">
                    Your free demo runs for 7 days. You can then continue with one of our messaging solutions that best suits your business needs.
                  </div>
                )}
              </div>

              {/* FAQ Item 3 */}
              <div className="border-b border-gray-200">
                <button 
                  className="w-full flex items-center justify-between py-4 text-left"
                  onClick={() => toggleFAQ(2)}
                >
                  <div className="flex items-center">
                    <div className="w-6 h-6 flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span className="text-lg font-medium">Why do you charge a monthly subscription vs PAYG per message pricing?</span>
                  </div>
                  <svg 
                    className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${openFAQ === 2 ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {/* Answer content */}
                {openFAQ === 2 && (
                  <div className="pb-4 px-10 text-gray-600 animate-fadeIn">
                    The messaging industry in Africa and globally is shifting from pay-as-you-go to subscription models because businesses want predictable costs and more value than just SMS. Rukuse's monthly plans offer free messages*, premium support, a best-in-class gateway and messaging expertise. This allows businesses to budget confidently while accessing the tools and insights they need to grow. Subscription pricing also helps us continuously invest in better technology and innovation to keep our customers ahead.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-6 text-sm text-gray-600">
        © Rukuse Technology (Private) Limited
      </div>
    </div>
  );
};

export default PackagesPage;

// Add new React components
const CampaignOptimizationPanel: React.FC = () => {
  // Implementation for campaign optimization UI
  return (
    <div>
      {/* Add your campaign optimization UI here */}
    </div>
  );
};

const WorkspaceManagement: React.FC = () => {
  // Implementation for workspace management UI
  return (
    <div>
      {/* Add your workspace management UI here */}
    </div>
  );
};

const ContactSyncDashboard: React.FC = () => {
  // Implementation for contact sync UI
  return (
    <div>
      {/* Add your contact sync UI here */}
    </div>
  );
}; 